import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { 
  Edit, 
  Trash2, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  Loader2, 
  User,
  Settings,
  Plus,
  Search,
  Filter,
  Sparkles,
  HelpCircle
} from 'lucide-react';

const AgentManager = ({ onShowHelp }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingAgent, setEditingAgent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    agent_type: '',
    llm: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Registration modal state
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationFormData, setRegistrationFormData] = useState({
    agent_name: '',
    description: '',
    agent_type: 'customer-support',
    llm: 'claude-3-5-sonnet-20241022',
    system_prompt: '',
    // New fields for external agent integration
    is_external_agent: false,
    external_agent_url: '',
    external_agent_webhook_url: '',
    external_agent_auth_token: '',
    external_agent_type: 'docker', // docker, api, sdk, custom
    auto_monitor: true
  });
  const [registering, setRegistering] = useState(false);

  // Available agent types and LLM models
  const agentTypes = [
    { value: 'customer-support', label: 'Customer Support', defaultPrompt: 'You are a helpful customer support agent. Be friendly, professional, and solve customer issues efficiently.' },
    { value: 'sales-assistant', label: 'Sales Assistant', defaultPrompt: 'You are a sales assistant. Help prospects understand product benefits and overcome objections professionally.' },
    { value: 'travel-assistant', label: 'Travel Assistant', defaultPrompt: 'You are a travel assistant. Provide accurate travel advice, recommendations, and booking assistance.' },
    { value: 'technical-support', label: 'Technical Support', defaultPrompt: 'You are a technical support specialist. Help users with technical issues using clear, step-by-step guidance.' },
    { value: 'general', label: 'General Assistant', defaultPrompt: 'You are a helpful AI assistant. Provide accurate, helpful responses to user questions.' },
    { value: 'content-creator', label: 'Content Creator', defaultPrompt: 'You are a content creator. Help users create engaging, informative content for various platforms.' },
    { value: 'data-analyst', label: 'Data Analyst', defaultPrompt: 'You are a data analyst. Help users understand and analyze data, create insights, and make data-driven decisions.' },
    { value: 'external-agent', label: 'External Agent (Docker/API)', defaultPrompt: 'You are an external AI agent. Provide helpful responses based on your specific domain and capabilities.' }
  ];

  const externalAgentTypes = [
    { value: 'docker', label: 'Docker Container' },
    { value: 'api', label: 'API Endpoint' },
    { value: 'sdk', label: 'SDK Integration' },
    { value: 'custom', label: 'Custom Integration' }
  ];

  const llmModels = [
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
  ];

  // Fetch user's agents
  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(getApiUrl('/user/agents'), {
        withCredentials: true
      });
      
      if (response.data && response.data.agents) {
        setAgents(response.data.agents);
        console.log(`✅ Fetched ${response.data.count} agents for user`);
      } else {
        setAgents([]);
        console.log('✅ No agents found for user');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to fetch agents');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  // Register new agent
  const handleRegisterAgent = async () => {
    try {
      setRegistering(true);
      setError('');
      
      // Get user's API key
      const apiKeyResponse = await axios.get(getApiUrl('/user/api-key'), {
        withCredentials: true
      });
      
      if (!apiKeyResponse.data.api_key) {
        throw new Error('No API key available. Please contact your administrator.');
      }
      
      const apiKey = apiKeyResponse.data.api_key;
      
      // Register the agent
      const response = await axios.post(
        getApiUrl('/agent/register'),
        registrationFormData,
        {
          headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 201) {
        const agentId = response.data.agent_id;
        setSuccess(`Agent registered successfully! Agent ID: ${agentId} (save this for monitoring)`);
        setShowRegistrationModal(false);
        setRegistrationFormData({
          agent_name: '',
          description: '',
          agent_type: 'customer-support',
          llm: 'claude-3-5-sonnet-20241022',
          system_prompt: '',
          // New fields for external agent integration
          is_external_agent: false,
          external_agent_url: '',
          external_agent_webhook_url: '',
          external_agent_auth_token: '',
          external_agent_type: 'docker', // docker, api, sdk, custom
          auto_monitor: true
        });
        fetchAgents(); // Refresh the list
      }
    } catch (err) {
      console.error('Error registering agent:', err);
      setError(err.response?.data?.error || 'Failed to register agent');
    } finally {
      setRegistering(false);
    }
  };

  // Handle agent type change to update default prompt
  const handleAgentTypeChange = (agentType) => {
    const selectedType = agentTypes.find(type => type.value === agentType);
    setRegistrationFormData({
      ...registrationFormData,
      agent_type: agentType,
      system_prompt: selectedType ? selectedType.defaultPrompt : ''
    });
  };

  // Update agent
  const handleUpdateAgent = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.put(
        getApiUrl(`/agent/${editingAgent.id}`),
        editFormData,
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        setSuccess('Agent updated successfully');
        setShowEditModal(false);
        setEditingAgent(null);
        fetchAgents(); // Refresh the list
      }
    } catch (err) {
      console.error('Error updating agent:', err);
      setError(err.response?.data?.error || 'Failed to update agent');
    } finally {
      setLoading(false);
    }
  };

  // Delete agent
  const handleDeleteAgent = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.delete(
        getApiUrl(`/agent/${agentToDelete.id}`),
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        setSuccess('Agent deleted successfully');
        setShowDeleteModal(false);
        setAgentToDelete(null);
        fetchAgents(); // Refresh the list
      }
    } catch (err) {
      console.error('Error deleting agent:', err);
      setError(err.response?.data?.error || 'Failed to delete agent');
    } finally {
      setLoading(false);
    }
  };

  // Rename agent
  const handleRenameAgent = async (agentId, newName) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.put(
        getApiUrl(`/agent/${agentId}/rename`),
        { name: newName },
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        setSuccess('Agent renamed successfully');
        fetchAgents(); // Refresh the list
      }
    } catch (err) {
      console.error('Error renaming agent:', err);
      setError(err.response?.data?.error || 'Failed to rename agent');
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (agent) => {
    setEditingAgent(agent);
    setEditFormData({
      name: agent.name,
      description: agent.description || '',
      agent_type: agent.agent_type,
      llm: agent.llm
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (agent) => {
    setAgentToDelete(agent);
    setShowDeleteModal(true);
  };

  // Filter and search agents
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (agent.description && agent.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterType === 'all' || agent.agent_type === filterType;
    return matchesSearch && matchesFilter;
  });

  // Get unique agent types for filter
  const existingAgentTypes = [...new Set(agents.map(agent => agent.agent_type))];

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Agent Manager</h1>
              <p className="text-gray-400">
                Manage your registered AI agents. View, edit, rename, and delete your agents.
              </p>
            </div>
            <div className="flex space-x-3">
              {onShowHelp && (
                <button
                  onClick={onShowHelp}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>Help</span>
                </button>
              )}
              <button
                onClick={() => setShowRegistrationModal(true)}
                className="flex items-center space-x-2 bg-[#7B61FF] text-white px-4 py-2 rounded-xl hover:bg-[#6B51EF] transition-colors duration-300"
              >
                <Plus className="w-4 h-4" />
                <span>Register New Agent</span>
              </button>
              <button
                onClick={fetchAgents}
                className="flex items-center space-x-2 bg-gray-700 text-white px-4 py-2 rounded-xl hover:bg-gray-600 transition-colors duration-300"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-900 border border-red-700 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-300">{error}</span>
            </div>
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div className="mb-6 p-4 bg-green-900 border border-green-700 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                <span className="text-green-300">{success}</span>
              </div>
              {success.includes('Agent ID:') && (
                <button
                  onClick={() => {
                    const agentId = success.match(/Agent ID: ([a-f0-9-]+)/)?.[1];
                    if (agentId) {
                      navigator.clipboard.writeText(agentId);
                      setSuccess('Agent ID copied to clipboard!');
                      setTimeout(() => setSuccess(''), 2000);
                    }
                  }}
                  className="px-3 py-1 bg-green-800 hover:bg-green-700 text-green-300 text-sm rounded-lg transition-colors"
                >
                  Copy ID
                </button>
              )}
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-600 rounded-xl bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
            >
              <option value="all">All Types</option>
              {existingAgentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Agents List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#7B61FF] mx-auto mb-4" />
            <p className="text-gray-400">Loading your agents...</p>
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">
              {agents.length === 0 ? 'No agents found. Register your first agent to get started.' : 'No agents match your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <div key={agent.id} className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-[#7B61FF] transition-colors duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">{agent.name}</h3>
                    {agent.description && (
                      <p className="text-gray-400 text-sm mb-3">{agent.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(agent)}
                      className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Edit Agent"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(agent)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete Agent"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Agent ID:</span>
                    <span className="text-white font-mono text-xs">{agent.id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Type:</span>
                    <span className="text-white font-medium">{agent.agent_type}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">LLM:</span>
                    <span className="text-white font-medium">{agent.llm}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Created:</span>
                    <span className="text-white">{formatDate(agent.created_at)}</span>
                  </div>
                  {agent.healing_recommended && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className="text-orange-400 font-medium">⚠️ Healing Recommended</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Registration Modal */}
        {showRegistrationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Register New Agent</h2>
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Agent Name *</label>
                    <input
                      type="text"
                      value={registrationFormData.agent_name}
                      onChange={(e) => setRegistrationFormData({...registrationFormData, agent_name: e.target.value})}
                      placeholder="Enter agent name"
                      className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Agent Type *</label>
                    <select
                      value={registrationFormData.agent_type}
                      onChange={(e) => handleAgentTypeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                    >
                      {agentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={registrationFormData.description}
                    onChange={(e) => setRegistrationFormData({...registrationFormData, description: e.target.value})}
                    placeholder="Describe what this agent does..."
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                  />
                </div>

                <div className="flex items-center p-4 bg-gray-700 rounded-xl border border-gray-600">
                  <input
                    type="checkbox"
                    id="is_external_agent"
                    checked={registrationFormData.is_external_agent}
                    onChange={(e) => setRegistrationFormData({...registrationFormData, is_external_agent: e.target.checked})}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 rounded"
                  />
                  <label htmlFor="is_external_agent" className="ml-3 text-sm text-gray-300">
                    <span className="font-medium">Connect External Agent</span>
                    <span className="block text-xs text-gray-500 mt-1">
                      This agent is running externally (Docker container, API endpoint, etc.) and I want to monitor its performance
                    </span>
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">LLM Model *</label>
                  <select
                    value={registrationFormData.llm}
                    onChange={(e) => setRegistrationFormData({...registrationFormData, llm: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                  >
                    {llmModels.map(model => (
                      <option key={model.value} value={model.value}>{model.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    System Prompt *
                    <span className="text-xs text-gray-500 ml-2">(Defines how the agent behaves)</span>
                  </label>
                  <textarea
                    value={registrationFormData.system_prompt}
                    onChange={(e) => setRegistrationFormData({...registrationFormData, system_prompt: e.target.value})}
                    placeholder="Enter the system prompt that defines how this agent should behave..."
                    rows="6"
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This prompt will be used every time the agent responds to ensure consistent behavior.
                  </p>
                </div>

                {registrationFormData.is_external_agent && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">External Agent URL *</label>
                        <input
                          type="text"
                          value={registrationFormData.external_agent_url}
                          onChange={(e) => setRegistrationFormData({...registrationFormData, external_agent_url: e.target.value})}
                          placeholder="e.g., http://localhost:5000"
                          className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">External Agent Type *</label>
                        <select
                          value={registrationFormData.external_agent_type}
                          onChange={(e) => setRegistrationFormData({...registrationFormData, external_agent_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                        >
                          {externalAgentTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">External Agent Webhook URL</label>
                      <input
                        type="text"
                        value={registrationFormData.external_agent_webhook_url}
                        onChange={(e) => setRegistrationFormData({...registrationFormData, external_agent_webhook_url: e.target.value})}
                        placeholder="e.g., https://your-webhook-endpoint.com/events"
                        className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">External Agent Auth Token</label>
                      <input
                        type="password"
                        value={registrationFormData.external_agent_auth_token}
                        onChange={(e) => setRegistrationFormData({...registrationFormData, external_agent_auth_token: e.target.value})}
                        placeholder="Enter a secure token for authentication"
                        className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="auto_monitor"
                        checked={registrationFormData.auto_monitor}
                        onChange={(e) => setRegistrationFormData({...registrationFormData, auto_monitor: e.target.checked})}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 rounded"
                      />
                      <label htmlFor="auto_monitor" className="ml-2 text-sm text-gray-300">
                        Auto-monitor this external agent for health and performance.
                      </label>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegisterAgent}
                  disabled={registering || !registrationFormData.agent_name || !registrationFormData.system_prompt}
                  className="flex-1 px-4 py-2 bg-[#7B61FF] text-white rounded-xl hover:bg-[#6B51EF] transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {registering ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Register Agent
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingAgent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-bold text-white mb-4">Edit Agent</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                  <input
                    type="text"
                    value={editFormData.agent_type}
                    onChange={(e) => setEditFormData({...editFormData, agent_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">LLM Model</label>
                  <input
                    type="text"
                    value={editFormData.llm}
                    onChange={(e) => setEditFormData({...editFormData, llm: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF]"
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateAgent}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-[#7B61FF] text-white rounded-xl hover:bg-[#6B51EF] transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Update Agent'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && agentToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-bold text-white mb-4">Delete Agent</h2>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete the agent "{agentToDelete.name}"? This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAgent}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Delete Agent'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentManager; 